import type { CellValue, WorksheetRow } from "@zebrabi/office-data";
import { DataView, DataViewMetadataColumn } from "@zebrabi/table-data";
import { MeasureRoles, tryGetMeasureRoleFromName } from "@zebrabi/data-helpers/fieldAssignment";
import { classifySourceColumns, isExcelTextCell } from "@zebrabi/data-helpers/sourceColumns";

/**
 * Assigns a measure role to every value field: by name where the header looks like one, then in the fixed order for the rest.
 * Writes the columns and roles into `dv.metadata` and returns `dv`.
 */
export function getMetadata(dv: DataView): DataView {
    let availableRoles = [MeasureRoles.Values, MeasureRoles.PreviousYear, MeasureRoles.Plan, MeasureRoles.Forecast, MeasureRoles.Comments];
    const usedRoles: MeasureRoles[] = [];
    const usedIndexes: number[] = [];
    const metaValueColumns: DataViewMetadataColumn[] = [];

    // auto assign measure roles
    dv.valueFields.forEach((vf, ix) => {
        const measureName = dv.valueNames[ix];
        const measureRole = tryGetMeasureRoleFromName(measureName);
        if (measureRole && !usedRoles.includes(measureRole)) {
            metaValueColumns.push(getMetaDataColumn(measureName, measureRole, ix));
            usedRoles.push(measureRole);
            availableRoles = availableRoles.filter((role) => role != measureRole);
            usedIndexes.push(ix);
        }
    });

    // assing remaining measures
    dv.valueFields.forEach((field, index) => {
        if (!usedIndexes.includes(index)) {
            const measureName = dv.valueNames[index];
            let role = availableRoles.shift();
            if (!role) {
                role = MeasureRoles.Values;
            }
            metaValueColumns.push(getMetaDataColumn(measureName, role, index));
            usedRoles.push(role);
            usedIndexes.push(index);
        }
    });

    // sort cols by index
    metaValueColumns.sort((c1, c2) => c1.index - c2.index);
    usedRoles.sort((r1, r2) => metaValueColumns.findIndex((c) => c.roles[r1]) - metaValueColumns.findIndex((c) => c.roles[r2]));

    dv.metadata.columns = metaValueColumns;
    dv.metadata.measureRoles = usedRoles;
    return dv;
}

function getMetaDataColumn(measureName: string, measureRole: MeasureRoles, index: number): DataViewMetadataColumn {
    return {
        displayName: measureName,
        roles: {
            [measureRole]: true,
        },
        type: measureRole === MeasureRoles.Comments ? { text: true } : { numeric: true },
        queryName: measureName,
        index: index,
    };
}

export function parseMultiMeasureDataViewWithGroups(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: any[][],
    dataValues: any[][]
): DataView {
    dataView.categories = [
        {
            values: rowLabelsValues.map((rlv) => rlv[0]),
            source: { displayName: dataView.rowNames[0] },
        },
    ];

    const columnLabelIndex = 1; // may need reordeing on Column fields on the Excel UI (possibly handling this could be improved)
    let groupNames = columnLabelsValues[columnLabelIndex].filter((name) => name !== "");

    if (dataView.rowGrandTotal) {
        // if grand total On, remove total column labels for other measures
        groupNames = groupNames.slice(0, 1 - dataView.valueNames.length);
    }

    dataView.values = dataValues[0].map((dv, index) => {
        return {
            values: dataValues.map((dv) => (isValidNonNullNumber(dv[index]) ? dv[index] : null)),
            source: {
                displayName: dataView.valueNames[index % dataView.valueNames.length],
                groupName: groupNames[~~(index / dataView.valueNames.length)],
            },
        };
    });

    dataView.values.grouped = function () {
        return groupNames //.slice(0, -1)
            .map((groupname) => {
                return {
                    name: groupname,
                    values: dataView.values.filter((v) => v.source.groupName === groupname),
                };
            });
    };

    return dataView;
}

export function parseMultiMeasureDataView(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: any[][],
    dataValues: any[][]
): DataView {
    dataView.categories = [
        {
            values: rowLabelsValues.map((rlv) => rlv[0]),
            source: { displayName: dataView.rowNames[0] },
        },
    ];

    dataView.values = dataValues[0].map((dv, index) => {
        return {
            values: dataValues.map((dv) => (isValidNonNullNumber(dv[index]) ? dv[index] : null)),
            source: {
                displayName: dataView.valueNames[index],
            },
        };
    });

    dataView.values.grouped = function () {
        return [{ values: dataView.values }];
    };
    return dataView;
}

export function parseRangeDataView(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: string[],
    dataValues: any[][],
    range: Excel.Range
): DataView {
    dataView.categories = [{ values: rowLabelsValues.map((rlv) => rlv[0]), source: { displayName: dataValues[0][0] || "Category" } }];
    dataView.values = [];
    const rangeValueTypes = range.valueTypes;

    for (let j = 1; j < range.columnCount; j++) {
        let values = dataValues.map((dv) => dv[j]);
        const valueTypes = rangeValueTypes.map((t) => t[j]);
        values.shift(); // remove header (1. row)
        valueTypes.shift();
        values = values.map((v, i) => (valueTypes[i] !== Excel.RangeValueType.error ? v : null));

        dataView.values.push({
            source: {
                displayName: dataValues[0][j],
            },
            values: values,
        });
    }

    dataView.values.grouped = function () {
        return [{ values: dataView.values }];
    };
    dataView.sourceColumns = classifySourceColumns(dataValues[0], dataValues.slice(1), isExcelTextCell, 1);

    return dataView;
}

export function parseRangeMultiplesDataView(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: string[],
    range: Excel.Range
): DataView {
    const dataValues = range.values;

    dataView.categories = [
        {
            values: rowLabelsValues.map((rlv) => rlv[0]),
            source: { displayName: dataValues[0][0] || "Category" },
        },
    ];

    dataView.values = [];
    const rangeValueTypes = range.valueTypes;
    for (let j = 1; j < range.columnCount; j++) {
        let values = dataValues.map((dv) => dv[j]);
        const valueTypes = rangeValueTypes.map((t) => t[j]);
        values.shift(); // remove header (1. row)
        valueTypes.shift();
        values = values.map((v, i) => (valueTypes[i] !== Excel.RangeValueType.error ? v : null));

        dataView.values.push({
            source: {
                displayName: "Values", //dataValues[0][j],
                groupName: columnLabelsValues[j - 1],
            },
            values: values,
        });
    }

    dataView.values.grouped = function () {
        return dataView.values.map((vals) => {
            return {
                name: vals.source.groupName,
                values: [vals],
            };
        });
    };

    dataView.sourceColumns = classifySourceColumns(dataValues[0], dataValues.slice(1), isExcelTextCell, 1);

    return dataView;
}

export function parseSingleMeasureDataViewWithGroups(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: any[][],
    dataValues: any[][]
): DataView {
    dataView.categories = [{ values: rowLabelsValues.map((rlv) => rlv[0]), source: { displayName: dataView.rowNames[0] } }];
    dataView.values = dataValues[0].map((dv, index) => {
        return {
            values: dataValues.map((dv) => (isValidNonNullNumber(dv[index]) ? dv[index] : null)),
            source: {
                displayName: dataView.valueNames[0],
                groupName: columnLabelsValues[1][index],
            },
        };
    });

    dataView.values.grouped = function () {
        return dataView.values.map((vals) => {
            return {
                name: vals.source.groupName,
                values: [vals],
            };
        });
    };

    return dataView;
}

export function parseSingleMeasureDataView(
    dataView: DataView,
    rowLabelsValues: any[][],
    columnLabelsValues: any[][],
    dataValues: any[][]
): DataView {
    dataView.categories = [{ values: rowLabelsValues.map((rlv) => rlv[0]), source: { displayName: dataView.rowNames[0] } }];
    dataView.values = [
        {
            values: dataValues.map((dv) => (isValidNonNullNumber(dv[0]) ? dv[0] : null)),
            source: { displayName: dataView.valueNames[0] },
        },
    ];
    dataView.values.grouped = function () {
        return [{ values: dataView.values }];
    };

    return dataView;
}

function isValidNonNullNumber(value: any) {
    return value !== null && value !== "";
}

/** The rows written back to the worksheet as sample data: a header row, then one row per category with a cell per measure. */
export function dataViewToWorksheetRows(dataView: DataView): WorksheetRow[] {
    const data: WorksheetRow[] = [];
    data.push([
        "Category",
        ...dataView.valueFields.map((field: string | Excel.PivotField) => (typeof field === "string" ? field : field.name)),
    ]);
    dataView.categories[0].values.forEach((c, i) => {
        const rowData: WorksheetRow = [c as CellValue];
        dataView.valueFields.forEach((v, j) => {
            rowData.push(dataView.values[j].values[i] as CellValue | null);
        });
        data.push(rowData);
    });
    return data;
}
